import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { createForest, createForestExercises, checkClassCodeExists } from '../../services/firestore';
import ExerciseSetup from './ExerciseSetup';
import Button from '../common/Button';
import { Forest } from '../../types';
import { updateDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { generateClassCode, isValidClassCodeFormat } from '../../utils/classCodeGenerator';

interface CreateForestModalProps {
  onClose: () => void;
  onForestCreated: () => void;
  teacherId: string;
}

type CreationStep = 'forest-details' | 'schedule-setup' | 'exercise-setup' | 'complete';

interface ForestFormData {
  name: string;
  slug: string;
  classCode: string;
  gameDuration: number;
  startDate: Date;
  startTime: string;
  startImmediately: boolean;
  maxStudents?: number;
  dailyWaterLimit?: number;
  studentWaterLimit?: number;
  gameWaterLimit?: number;
  isQuickGame: boolean;
}

const CreateForestModal: React.FC<CreateForestModalProps> = ({
  onClose,
  onForestCreated,
  teacherId
}) => {
  const [currentStep, setCurrentStep] = useState<CreationStep>('forest-details');
  const [forestData, setForestData] = useState<ForestFormData>({
    name: '',
    slug: '',
    classCode: '',
    gameDuration: 10,
    startDate: new Date(),
    startTime: '09:00',
    startImmediately: false,
    maxStudents: undefined,
    dailyWaterLimit: undefined,
    studentWaterLimit: undefined,
    gameWaterLimit: undefined,
    isQuickGame: false,
  });
  const [createdForest, setCreatedForest] = useState<Forest | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [classCodeGenerated, setClassCodeGenerated] = useState(false);

  useEffect(() => {
    if (!classCodeGenerated) {
      const newCode = generateClassCode();
      setForestData(prev => ({ ...prev, classCode: newCode }));
      setClassCodeGenerated(true);
    }
  }, [classCodeGenerated]);

  const goToNextStep = async () => {
    switch (currentStep) {
      case 'forest-details':
        const isValid = await validateForestDetails();
        if (isValid) setCurrentStep('schedule-setup');
        break;
      case 'schedule-setup':
        if (validateSchedule()) handleCreateForest();
        break;
    }
  };

  const goToPreviousStep = () => {
    switch (currentStep) {
      case 'schedule-setup':
        setCurrentStep('forest-details');
        break;
      case 'exercise-setup':
        setCurrentStep('schedule-setup');
        break;
    }
  };

  const validateForestDetails = async (): Promise<boolean> => {
    const newErrors: Record<string, string> = {};
    
    if (!forestData.name.trim()) newErrors.name = 'Forest name is required';
    
    if (!forestData.classCode.trim()) {
      newErrors.classCode = 'Class code is required';
    } else {
      const normalizedCode = forestData.classCode.trim().toUpperCase();
      
      if (!isValidClassCodeFormat(normalizedCode)) {
        newErrors.classCode = 'Class code must be in format: XXXX-XXXX (e.g., HOPE-A7K2)';
      } else {
        const codeExists = await checkClassCodeExists(normalizedCode);
        if (codeExists) {
          newErrors.classCode = 'This class code is already in use. Please choose another.';
        }
      }
    }
    
    if (forestData.gameDuration < 1 || forestData.gameDuration > 30) {
      newErrors.gameDuration = 'Game duration must be between 1 and 30 days';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateSchedule = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    //Skip validation if "Start Immediately" is checked
    if (forestData.startImmediately) {
      setErrors(newErrors);
      return true;
    }
    
    //Only validate date/time if NOT starting immediately
    const now = new Date();
    const selectedDateTime = new Date(forestData.startDate);
    const [hours, minutes] = forestData.startTime.split(':').map(Number);
    selectedDateTime.setHours(hours, minutes, 0, 0);
    
    if (selectedDateTime < now) {
      newErrors.startDate = 'Start date and time must be in the future';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  //Create forest
const handleCreateForest = async () => {
  setIsSubmitting(true);
  try {
    let startDateTime: Date;
    
    if (forestData.startImmediately) {
      startDateTime = new Date();
    } else {
      startDateTime = new Date(forestData.startDate);
      const [hours, minutes] = forestData.startTime.split(':').map(Number);
      startDateTime.setHours(hours, minutes, 0, 0);
    }
    
    let endDate: Date;
    if (forestData.isQuickGame) {
      endDate = new Date(startDateTime.getTime() + (forestData.gameDuration * 60 * 1000));
      console.log(`Quick Game: ${forestData.gameDuration} minutes duration`);
      console.log(`Start: ${startDateTime.toLocaleString()}`);
      console.log(`End: ${endDate.toLocaleString()}`);
    } else {
      endDate = new Date(startDateTime);
      endDate.setDate(startDateTime.getDate() + forestData.gameDuration);
    }
    
    const normalizedClassCode = forestData.classCode.trim().toUpperCase();
    
    const forest = await createForest({
      name: forestData.name,
      slug: forestData.slug || forestData.name.toLowerCase().replace(/\s+/g, '-'),
      classCode: normalizedClassCode,
      teacherId,
      dailyExerciseGroupId: '',
      gameDuration: forestData.gameDuration,
      startDate: startDateTime,
      endDate: endDate,
      maxStudents: forestData.maxStudents || null,
      dailyWaterLimit: forestData.dailyWaterLimit || null,
      studentWaterLimit: forestData.studentWaterLimit || null,
      gameWaterLimit: forestData.gameWaterLimit || null,
      isActive: false,
      isDraft: true,
      currentStudents: 0,
      isQuickGame: forestData.isQuickGame,
      timeMultiplier: forestData.isQuickGame ? 1440 : 1,
    });
    
    setCreatedForest(forest);
    setCurrentStep('exercise-setup');
  } catch (error: any) {
    setErrors({ submit: error.message || 'Failed to create forest' });
  } finally {
    setIsSubmitting(false);
  }
};



  const handleExercisesComplete = async (exercises: Array<{
    day: number;
    mediaType: 0 | 1 | 2 | 3 | 4 | 5;
    title: string;
    instructions: string;
    mediaUrl: string;
  }>) => {
    if (!createdForest) return;
    
    try {
      const groupId = await createForestExercises(createdForest.id, exercises);
      
      console.log('Exercises created with group ID:', groupId);
      
      const forestDoc = await getDoc(doc(db, 'forests', createdForest.id));
      const updatedForest = forestDoc.data();
      
      if (!updatedForest?.dailyExerciseGroupId) {
        throw new Error('Forest was not updated with exercise group ID');
      }
      
      await updateDoc(doc(db, 'forests', createdForest.id), {
        isDraft: false,
        isActive: true,
        updatedAt: serverTimestamp(),
      });
      
      onForestCreated();
    } catch (error: any) {
      console.error('Error creating exercises:', error);
      setErrors({ submit: error.message || 'Failed to create exercises' });
    }
  };

  //Exercise setup
  if (currentStep === 'exercise-setup' && createdForest) {
    return (
      <ExerciseSetup
        forestId={createdForest.id}
        forestName={createdForest.name}
        gameDuration={createdForest.gameDuration}
        onComplete={handleExercisesComplete}
        onBack={goToPreviousStep}
      />
    );
  }

  const getStepNumber = () => {
    switch (currentStep) {
      case 'forest-details': return 1;
      case 'schedule-setup': return 2;
      case 'exercise-setup': return 3;
      default: return 1;
    }
  };

  //Quick Ganme Duration
  const getQuickGameDuration = () => {
    return forestData.isQuickGame ? `${forestData.gameDuration} minutes` : `${forestData.gameDuration} days`;
  };

  //Main modal
  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-powder-800">Create New Forest</h2>
            <button
              onClick={onClose}
              className="text-powder-400 hover:text-powder-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center space-x-4">
              {/* Step 1: Forest Details */}
              <div className={`flex items-center space-x-2 ${
                getStepNumber() >= 1 ? 'text-sage-600' : 'text-powder-400'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                  getStepNumber() >= 1 ? 'bg-sage-500' : 'bg-powder-300'
                }`}>
                  1
                </div>
                <span className="font-medium text-sm">Details</span>
              </div>
              
              <div className="flex-1 h-px bg-powder-200"></div>
              
              {/* Step 2: Schedule */}
              <div className={`flex items-center space-x-2 ${
                getStepNumber() >= 2 ? 'text-sage-600' : 'text-powder-400'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                  getStepNumber() >= 2 ? 'bg-sage-500' : 'bg-powder-300'
                }`}>
                  2
                </div>
                <span className="font-medium text-sm">Schedule</span>
              </div>

              <div className="flex-1 h-px bg-powder-200"></div>
              
              {/* Step 3: Exercises */}
              <div className={`flex items-center space-x-2 ${
                getStepNumber() >= 3 ? 'text-sage-600' : 'text-powder-400'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                  getStepNumber() >= 3 ? 'bg-sage-500' : 'bg-powder-300'
                }`}>
                  3
                </div>
                <span className="font-medium text-sm">Exercises</span>
              </div>
            </div>
          </div>

          {/* Step Content */}
          <div className="space-y-6">
            {currentStep === 'forest-details' && (
              <motion.div
                key="forest-details"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h3 className="text-lg font-semibold text-powder-800 mb-4">Forest Details</h3>
                
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-powder-700 mb-2">
                      Forest Name *
                    </label>
                    <input
                      type="text"
                      value={forestData.name}
                      onChange={(e) => setForestData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="My Awesome Forest"
                      className="w-full px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500"
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-powder-700 mb-2">
                      Class Code *
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={forestData.classCode}
                        onChange={(e) => {
                          setForestData(prev => ({ ...prev, classCode: e.target.value.toUpperCase() }));
                          if (errors.classCode) {
                            setErrors(prev => ({ ...prev, classCode: '' }));
                          }
                        }}
                        placeholder="HOPE-A7K2"
                        className="flex-1 px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newCode = generateClassCode();
                          setForestData(prev => ({ ...prev, classCode: newCode }));
                          if (errors.classCode) {
                            setErrors(prev => ({ ...prev, classCode: '' }));
                          }
                        }}
                        className="px-3 py-2 bg-powder-200 hover:bg-powder-300 text-powder-700 rounded-lg transition-colors text-sm font-medium"
                        title="Generate new code"
                      >
                        🔄
                      </button>
                    </div>
                    <p className="text-xs text-powder-500 mt-1">
                      Auto-generated for you, but you can change it to anything unique
                    </p>
                    {errors.classCode && <p className="text-red-500 text-sm mt-1">{errors.classCode}</p>}
                  </div>
                </div>

                {/* Game Duration */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-powder-700 mb-2">
                    Game Duration (Days) *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={forestData.gameDuration}
                    onChange={(e) => setForestData(prev => ({ ...prev, gameDuration: parseInt(e.target.value) || 10 }))}
                    className="w-full px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500"
                  />
                  <p className="text-sm text-powder-500 mt-1">
                    You'll create {forestData.gameDuration} daily exercises in step 3
                  </p>
                  {errors.gameDuration && <p className="text-red-500 text-sm mt-1">{errors.gameDuration}</p>}
                </div>

                {/* Optional Settings */}
                <div>
                  <h4 className="font-medium text-powder-800 mb-3">Optional Limits</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-powder-700 mb-2">
                        Max Students
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={forestData.maxStudents || ''}
                        onChange={(e) => setForestData(prev => ({ 
                          ...prev, 
                          maxStudents: e.target.value ? parseInt(e.target.value) : undefined 
                        }))}
                        placeholder="Unlimited"
                        className="w-full px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-powder-700 mb-2">
                        Daily Water Limit
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={forestData.dailyWaterLimit || ''}
                        onChange={(e) => setForestData(prev => ({ 
                          ...prev, 
                          dailyWaterLimit: e.target.value ? parseInt(e.target.value) : undefined 
                        }))}
                        placeholder="Unlimited"
                        className="w-full px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-powder-700 mb-2">
                        Student Water Limit
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={forestData.studentWaterLimit || ''}
                        onChange={(e) => setForestData(prev => ({ 
                          ...prev, 
                          studentWaterLimit: e.target.value ? parseInt(e.target.value) : undefined 
                        }))}
                        placeholder="Unlimited"
                        className="w-full px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {currentStep === 'schedule-setup' && (
              <motion.div
                key="schedule-setup"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h3 className="text-lg font-semibold text-powder-800 mb-4">Schedule Setup</h3>
                
                {/* Start Immediately Option */}
                <div className="mb-6">
                  <div className="flex items-center space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="startImmediately"
                      checked={forestData.startImmediately}
                      onChange={(e) => setForestData(prev => ({ 
                        ...prev, 
                        startImmediately: e.target.checked 
                      }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <label htmlFor="startImmediately" className="text-sm font-medium text-blue-800 cursor-pointer">
                        Start Immediately
                      </label>
                      <p className="text-xs text-blue-700 mt-1">
                        Begin the forest right now instead of scheduling for later
                      </p>
                    </div>
                  </div>
                </div>

                {/* Date and Time Selection (disabled if Start Immediately is checked) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-powder-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={forestData.startDate.toISOString().split('T')[0]}
                      onChange={(e) => setForestData(prev => ({ 
                        ...prev, 
                        startDate: new Date(e.target.value) 
                      }))}
                      min={new Date().toISOString().split('T')[0]}
                      disabled={forestData.startImmediately}
                      className={`w-full px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 ${
                        forestData.startImmediately ? 'bg-gray-100 text-gray-400' : ''
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-powder-700 mb-2">
                      Start Time * (Eastern Time)
                    </label>
                    <input
                      type="time"
                      value={forestData.startTime}
                      onChange={(e) => setForestData(prev => ({ 
                        ...prev, 
                        startTime: e.target.value 
                      }))}
                      disabled={forestData.startImmediately}
                      className={`w-full px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 ${
                        forestData.startImmediately ? 'bg-gray-100 text-gray-400' : ''
                      }`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      All times are in Eastern Time Zone (EST/EDT)
                    </p>
                  </div>
                </div>

                {errors.startDate && <p className="text-red-500 text-sm mt-2">{errors.startDate}</p>}

                {/* Quick Game Mode Section */}
                <div className="mt-6">
                  <div className="flex items-start space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="quickGameMode"
                      checked={forestData.isQuickGame}
                      onChange={(e) => setForestData(prev => ({ 
                        ...prev, 
                        isQuickGame: e.target.checked 
                      }))}
                      className="mt-1 h-4 w-4 text-sage-600 focus:ring-sage-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <label htmlFor="quickGameMode" className="text-sm font-medium text-yellow-800 cursor-pointer">
                        Quick Game Mode (Testing Only)
                      </label>
                      <p className="text-xs text-yellow-700 mt-1">
                        {forestData.isQuickGame ? (
                          <>
                            <strong>⚡ Quick Mode Active:</strong> This forest will run at 1440x speed. 
                            Each "day" will be 1 minute in real time. Total duration: {forestData.gameDuration} minutes.
                          </>
                        ) : (
                          'Enable this to compress the timeline for rapid testing. 1 day = 1 minute. Not recommended for classroom use.'
                        )}
                      </p>
                      {forestData.isQuickGame && (
                        <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-800">
                          <strong>Warning:</strong> Quick mode is for development and testing only. 
                          All game mechanics (tree growth, exercise unlocking, etc.) will run at accelerated speed.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Schedule Summary */}
                <div className="mt-6 p-4 bg-sage-50 rounded-lg border border-sage-200">
                  <h4 className="font-medium text-sage-800 mb-2">Schedule Summary</h4>
                  <div className="text-sm text-sage-700 space-y-1">
                    {forestData.startImmediately ? (
                      <>
                        <p><strong>Start:</strong> Immediately upon creation</p>
                        <p><strong>Duration:</strong> {forestData.isQuickGame ? `${forestData.gameDuration} minutes` : `${forestData.gameDuration} days`}</p>
                        <p><strong>End:</strong> {forestData.isQuickGame ? 'Approx. ' + new Date(Date.now() + forestData.gameDuration * 60 * 1000).toLocaleTimeString() : 'Approx. ' + new Date(Date.now() + forestData.gameDuration * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
                      </>
                    ) : (
                      <>
                        <p><strong>Start:</strong> {forestData.startDate.toLocaleDateString()} at {forestData.startTime} ET</p>
                        <p><strong>Duration:</strong> {forestData.isQuickGame ? `${forestData.gameDuration} minutes` : `${forestData.gameDuration} days`}</p>
                        <p><strong>End:</strong> {new Date(
                          forestData.startDate.getTime() + 
                          (forestData.gameDuration * (forestData.isQuickGame ? 60 * 1000 : 24 * 60 * 60 * 1000))
                        ).toLocaleDateString()}</p>
                      </>
                    )}
                    {forestData.isQuickGame && <span className="text-yellow-600 font-medium"> (⚡ Quick Mode)</span>}
                  </div>
                </div>
              </motion.div>
            )}

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-700 text-sm">{errors.submit}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between mt-8 pt-6 border-t border-powder-200">
            <div>
              {currentStep !== 'forest-details' && (
                <Button
                  variant="outline"
                  onClick={goToPreviousStep}
                  disabled={isSubmitting}
                >
                  ← Previous
                </Button>
              )}
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={goToNextStep}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 
                 currentStep === 'forest-details' ? 'Next: Schedule' :
                 currentStep === 'schedule-setup' ? 'Next: Setup Exercises' : 'Complete'}
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CreateForestModal;